import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublishTestimonialMutation, useGetVideoTestimonialByIdQuery, useUpdateReviewResponseMutation, useToggleFeatureTestimonialMutation } from '../../store/vendorApi';
import { ArrowLeft, Star, Building, CheckCircle, Video, Play, Download, Calendar, MessageSquare, Wand2, Edit3, Send, Sparkles, Clock, AlertTriangle } from 'lucide-react';
import { TestimonialResponse } from '@clareefai/types';
import { VideoModal } from '@clareefai/ui';

const TestimonialDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();

  // Get testimonial from location state
  const testimonialFromState = location.state?.testimonial as TestimonialResponse | undefined;

  // Fetch the testimonial using the new endpoint if we have promoterId, otherwise skip
  const { data: testimonialFromApi, isLoading } = useGetVideoTestimonialByIdQuery(
    { promoterId: testimonialFromState?.promoterId || '', testimonialId: id || '' },
    { skip: !testimonialFromState?.promoterId || !id }
  );

  const displayTestimonial = testimonialFromApi || testimonialFromState;

  const [showResponseSection, setShowResponseSection] = useState(false);
  const [responseMode, setResponseMode] = useState<'ai' | 'manual'>('ai');
  const [responseText, setResponseText] = useState(displayTestimonial?.vendorResponse || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);

  const [publishTestimonial, { isLoading: isPublishing }] = usePublishTestimonialMutation();
  const [updateTestimonialResponse, { isLoading: isSavingResponse }] = useUpdateReviewResponseMutation();
  const [toggleFeature, { isLoading: isTogglingFeature }] = useToggleFeatureTestimonialMutation();

  useEffect(() => {
    if (displayTestimonial) {
      setResponseText(displayTestimonial.vendorResponse || '');
    }
  }, [displayTestimonial]);

  const handleClose = () => {
    navigate('/testimonials');
  };

  const downloadVideo = async (url: string, filename = 'testimonial.mp4') => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  const generateAIResponse = async () => {
    if (!displayTestimonial) return;

    setIsGenerating(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    const aiResponse = t('testimonials.details.aiGenerationMessage', {
      name: displayTestimonial.promoterName,
      product: displayTestimonial.productId || t('testimonials.details.aiGenerationProduct'),
      company: displayTestimonial.promoterCompany || t('testimonials.details.aiGenerationYou'),
    });

    setResponseText(aiResponse);
    setIsGenerating(false);
  };

  const handleSaveResponse = async () => {
    if (!displayTestimonial || !id) return;
    try {
      await updateTestimonialResponse({
        promoterId: displayTestimonial.promoterId,
        testimonialId: id,
        request: { response: responseText }
      }).unwrap();
      setShowResponseSection(false);
    } catch (error) {
      console.error('Error saving response:', error);
      alert(t('testimonials.details.saveResponseError'));
    }
  };

  const handlePublish = async (isPublished: boolean) => {
    if (!displayTestimonial || !id) return;
    try {
      await publishTestimonial({ promoterId: displayTestimonial.promoterId, testimonialId: id, isPublished }).unwrap();
    } catch (error) {
      console.error('Error updating publish status:', error);
    }
  };

  const handleToggleFeatured = async () => {
    if (!displayTestimonial || !id) return;
    try {
      await toggleFeature({
        promoterId: displayTestimonial.promoterId,
        testimonialId: id
      }).unwrap();
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert(t('testimonials.details.toggleFeaturedError'));
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      COMPLETED: 'bg-gray-100 text-gray-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      FEATURED: 'bg-purple-100 text-purple-800'
    };

    const labels: Record<string, string> = {
      COMPLETED: t('testimonials.status.completed'),
      PUBLISHED: t('testimonials.status.published'),
      FEATURED: t('testimonials.status.featured')
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getQualityBadge = (quality?: string) => {
    if (!quality) return null;

    const badges = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    };

    const qualityLabels: Record<string, string> = {
      high: t('testimonials.details.qualityHigh'),
      medium: t('testimonials.details.qualityMedium'),
      low: t('testimonials.details.qualityLow'),
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[quality as keyof typeof badges]}`}>
        {t('testimonials.details.qualityBadge', { quality: qualityLabels[quality] || quality })}
      </span>
    );
  };

  if (!displayTestimonial) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4339A0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('testimonials.details.loading')}</h2>
          <button onClick={() => navigate('/testimonials')} className="text-[#4339A0] hover:text-[#3d2f8f] font-medium">
            {t('testimonials.details.backToTestimonials')}
          </button>
        </div>
      </div>
    );
  }

  const promoterInitials = displayTestimonial.promoterName.split(' ').map(n => n[0]).join('');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={handleClose}
              className="p-2 -ml-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('testimonials.details.backToTestimonials')}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              {t('testimonials.details.heading')}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(displayTestimonial.status)}
            {displayTestimonial.quality && getQualityBadge(displayTestimonial.quality)}
          </div>
        </div>

        {/* Two-column layout: main content + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {displayTestimonial.videoUrl && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Video className="w-5 h-5 mr-2 text-blue-600" />
                  {t('testimonials.details.videoTestimonial')}
                </h3>

                {/* Video Title */}
                {displayTestimonial.videoTitle && (
                  <h4 className="text-xl font-bold text-gray-900 mb-4">
                    {displayTestimonial.videoTitle}
                  </h4>
                )}

                <div
                  className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden flex flex-col items-center justify-center cursor-pointer group"
                  onClick={() => setPlayingVideoUrl(displayTestimonial.videoUrl || null)}
                >
                  <video
                    src={`${displayTestimonial.videoUrl}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                  <div className="relative z-10 w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center group-hover:bg-blue-700 group-hover:scale-105 transition-all mb-3">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <p className="relative z-10 text-white text-sm font-medium">{t('testimonials.details.clickToPlay')}</p>
                </div>

                {/* Video Description */}
                {displayTestimonial.videoDescription && (
                  <div className="mt-5">
                    <h4 className="font-medium text-gray-700 mb-2">{t('testimonials.details.description')}</h4>
                    <p className="text-gray-600 leading-relaxed">{displayTestimonial.videoDescription}</p>
                  </div>
                )}

                {/* Video Tags */}
                {displayTestimonial.videoTags && displayTestimonial.videoTags.length > 0 && (
                  <div className="mt-5">
                    <h4 className="font-medium text-gray-700 mb-2">{t('testimonials.details.tags')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {displayTestimonial.videoTags.map((tag, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {displayTestimonial.content && (
                  <div className="mt-5 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                    <h4 className="font-medium text-blue-900 mb-2">{t('testimonials.details.additionalContent')}</h4>
                    <p className="text-blue-800 italic">"{displayTestimonial.content}"</p>
                  </div>
                )}

                <button
                  onClick={() => downloadVideo(displayTestimonial.videoUrl!, `${displayTestimonial.promoterName}-testimonial.mp4`)}
                  className="mt-5 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {t('testimonials.details.downloadVideo')}
                </button>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('testimonials.details.companyResponse')}</h3>
                <button
                  onClick={() => setShowResponseSection(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>{displayTestimonial.vendorResponse ? t('testimonials.details.editResponse') : t('testimonials.details.addResponse')}</span>
                </button>
              </div>

              {displayTestimonial.vendorResponse && !showResponseSection ? (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <Building className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-blue-900">{t('testimonials.details.companyResponse')}</span>
                  </div>
                  <p className="text-blue-800">{displayTestimonial.vendorResponse}</p>
                </div>
              ) : !showResponseSection ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('testimonials.details.noResponseYet')}</p>
                  <p className="text-sm">{t('testimonials.details.clickToAddResponse')}</p>
                </div>
              ) : null}

              {showResponseSection && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    {displayTestimonial.vendorResponse ? t('testimonials.details.editResponseHeading') : t('testimonials.details.addResponseHeading')}
                  </h4>

                  <div className="mb-6">
                    <h5 className="font-medium text-gray-900 mb-3">{t('testimonials.details.responseMode')}</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setResponseMode('ai')}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          responseMode === 'ai'
                            ? 'border-[#4339A0] bg-blue-50'
                            : 'border-gray-200 hover:border-[#4339A0]'
                        }`}
                      >
                        <div className="text-center">
                          <Wand2 className="w-8 h-8 text-[#4339A0] mx-auto mb-2" />
                          <h6 className="font-medium text-gray-900">{t('testimonials.details.aiGenerated')}</h6>
                          <p className="text-xs text-gray-600">{t('testimonials.details.aiGeneratedHint')}</p>
                        </div>
                      </button>

                      <button
                        onClick={() => setResponseMode('manual')}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          responseMode === 'manual'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-center">
                          <Edit3 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                          <h6 className="font-medium text-gray-900">{t('testimonials.details.manual')}</h6>
                          <p className="text-xs text-gray-600">{t('testimonials.details.manualHint')}</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {responseMode === 'ai' && (
                    <div className="mb-6">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center space-x-3 mb-3">
                          <Sparkles className="w-5 h-5 text-[#4339A0]" />
                          <h6 className="font-medium text-gray-900">{t('testimonials.details.aiResponseGenerator')}</h6>
                        </div>
                        <p className="text-sm text-gray-800 mb-4">
                          {t('testimonials.details.aiResponseDescription')}
                        </p>
                        <button
                          onClick={generateAIResponse}
                          disabled={isGenerating}
                          className="w-full bg-[#4339A0] text-white px-4 py-2 rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                          {isGenerating ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>{t('testimonials.details.generating')}</span>
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4" />
                              <span>{t('testimonials.details.generateResponse')}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('testimonials.details.responseText')}
                    </label>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      rows={8}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder={responseMode === 'ai'
                        ? t('testimonials.details.responseAiPlaceholder')
                        : t('testimonials.details.responseManualPlaceholder')
                      }
                    />
                  </div>

                  {responseText && (
                    <div className="mb-6">
                      <h6 className="font-medium text-gray-900 mb-2">{t('testimonials.details.preview')}</h6>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <Building className="w-3 h-3 text-white" />
                          </div>
                          <span className="font-medium text-blue-900">{t('testimonials.details.responsePreview')}</span>
                        </div>
                        <p className="text-blue-800 text-sm">{responseText}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowResponseSection(false)}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleSaveResponse}
                      disabled={!responseText.trim() || isSavingResponse}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {isSavingResponse ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('testimonials.details.saving')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>{t('testimonials.details.saveResponse')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Sidebar */}
          <div className="space-y-6 lg:sticky lg:top-6">
            {/* Review actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">{t('testimonials.details.reviewActions')}</h3>
              <div className="flex flex-col gap-3">
                {displayTestimonial.status === 'COMPLETED' ? (
                  <button
                    onClick={() => handlePublish(true)}
                    disabled={isPublishing}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {isPublishing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('testimonials.details.publishing')}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>{t('testimonials.details.publishTestimonial')}</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePublish(false)}
                    disabled={isPublishing}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {isPublishing ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('testimonials.details.updating')}</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5" />
                        <span>{t('testimonials.details.unpublishTestimonial')}</span>
                      </>
                    )}
                  </button>
                )}

                {(displayTestimonial.status === 'PUBLISHED' || displayTestimonial.status === 'FEATURED') && (
                  <button
                    onClick={handleToggleFeatured}
                    disabled={isTogglingFeature}
                    className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      displayTestimonial.isFeatured
                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {isTogglingFeature ? (
                      <>
                        <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                        <span>{t('testimonials.details.updating')}</span>
                      </>
                    ) : (
                      <>
                        <Star className="w-5 h-5" />
                        <span>{displayTestimonial.isFeatured ? t('testimonials.details.removeFromFeatured') : t('testimonials.details.makeFeatured')}</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {displayTestimonial.status === 'COMPLETED' && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">{t('testimonials.details.notPublished')}</h4>
                      <p className="text-sm text-gray-800 mt-1">
                        {t('testimonials.details.notPublishedDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Promoter information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">{t('testimonials.details.promoterInformation')}</h3>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 shrink-0 bg-[#4339A0]/10 rounded-full flex items-center justify-center text-[#4339A0] font-semibold text-lg border-2 border-[#4339A0]">
                  {promoterInitials}
                </div>
                <div className="min-w-0">
                  <h4 className="text-lg font-bold text-gray-900 truncate">{displayTestimonial.promoterName}</h4>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                    <Building className="w-4 h-4 shrink-0" />
                    <span className="truncate">{displayTestimonial.promoterCompany || t('testimonials.details.noCompany')}</span>
                  </p>
                </div>
              </div>
              {displayTestimonial.createdAt && (
                <div className="flex items-center text-sm text-gray-500 mt-4 pt-4 border-t border-gray-100">
                  <Calendar className="w-4 h-4 mr-2 shrink-0" />
                  {new Date(displayTestimonial.createdAt).toLocaleDateString('en-US')}
                </div>
              )}
            </div>

            {/* Product information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">{t('testimonials.details.productInformation')}</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500 mb-0.5">{t('testimonials.details.product')}</dt>
                  <dd className="text-sm font-medium text-gray-900">{displayTestimonial.productId || t('testimonials.details.naShort')}</dd>
                </div>
                {displayTestimonial.createdAt && (
                  <div>
                    <dt className="text-xs text-gray-500 mb-0.5">{t('testimonials.details.submitted')}</dt>
                    <dd className="text-sm font-medium text-gray-900">{new Date(displayTestimonial.createdAt).toLocaleDateString('en-US')}</dd>
                  </div>
                )}
              </dl>

              {displayTestimonial.notes && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <MessageSquare className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-800 text-sm">{displayTestimonial.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={!!playingVideoUrl}
        onClose={() => setPlayingVideoUrl(null)}
        videoUrl={playingVideoUrl || ''}
        title={displayTestimonial.videoTitle}
        description={displayTestimonial.videoDescription}
        tags={displayTestimonial.videoTags}
      />
    </div>
  );
};

export default TestimonialDetails;
